import CloseIcon from "@mui/icons-material/Close";
import { Dialog, Skeleton, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { QRCode } from "../../constant/ImagesS3.js";
import {
  dialogSocialIcons,
  downloadAppLinks,
} from "../../constant/staticData.js";
import { useDialog } from "../../context/DialogContext.js";

const ui = {
  dialog: (isSmallScreen) => ({
    "& .MuiPaper-root": {
      width: isSmallScreen ? "92%" : "440px",
      maxWidth: "440px",
      borderRadius: "24px",
      padding: 0,
      margin: "0 12px",
      overflow: "hidden",
      boxShadow:
        "0 24px 60px rgba(15, 40, 90, 0.18), 0 0 0 1px rgba(21,112,239,0.06)",
    },
  }),
  backdrop: {
    backdropFilter: "blur(4px)",
    backgroundColor: "rgba(10, 14, 24, 0.55)",
  },
  banner: {
    position: "relative",
    background:
      "linear-gradient(135deg, #1570ef 0%, #0d5fd6 55%, #0a4bb0 100%)",
    color: "#fff",
    padding: "26px 28px 22px",
  },
  close: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.18)",
    color: "#fff",
    cursor: "pointer",
    padding: "4px",
    transition: "background 0.15s ease",
  },
  eyebrowPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#fff",
    background: "rgba(255,255,255,0.18)",
    padding: "5px 12px",
    borderRadius: 999,
    fontFamily: "Lexend, sans-serif",
  },
  pulse: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "0 0 0 0 rgba(255,255,255,0.7)",
  },
  headline: {
    margin: "12px 0 6px",
    fontFamily: "Lexend, sans-serif",
    fontSize: 24,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: "-0.015em",
  },
  sub: {
    margin: 0,
    fontFamily: "Lexend, sans-serif",
    fontSize: 14,
    lineHeight: 1.55,
    color: "rgba(255,255,255,0.88)",
  },
  body: {
    padding: "24px 28px 22px",
    background: "#fff",
  },
  qrShell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    padding: "16px",
    borderRadius: 18,
    background: "#f6f9ff",
    border: "1px solid #e5e9f0",
  },
  qrLabel: {
    fontFamily: "Lexend, sans-serif",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#1570ef",
  },
  qrImg: {
    width: 170,
    height: 170,
    borderRadius: 10,
    background: "#fff",
    padding: 6,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "20px 0 14px",
    color: "#9aa3b2",
    fontFamily: "Lexend, sans-serif",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: "#e5e9f0",
  },
  storeRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
  },
  storeImg: {
    height: 42,
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
  },
  socialBlock: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid #eef1f6",
    textAlign: "center",
  },
  socialLabel: {
    fontFamily: "Lexend, sans-serif",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#5b6473",
    marginBottom: 10,
  },
  socialRow: {
    display: "flex",
    justifyContent: "center",
    gap: 10,
  },
  socialIcon: {
    width: 34,
    height: 34,
    cursor: "pointer",
  },
};

function CustomDialog({ isDialogOpen, handleClose, isSuccessful }) {
  const { handleDialogClose } = useDialog();
  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const total = (downloadAppLinks?.length || 0) + (dialogSocialIcons?.length || 0) + 1;
    setTotalImages(total);
  }, []);

  const handleModalClose = () => {
    handleClose();
    handleDialogClose();
  };

  const handleImageLoad = () => {
    setImagesLoaded((prevCount) => prevCount + 1);
  };

  useEffect(() => {
    if (imagesLoaded === totalImages) {
      setTimeout(() => setIsLoading(false), 800);
    }
  }, [imagesLoaded, totalImages]);

  if (!isDialogOpen) return null;

  const headline = isSuccessful
    ? "You're in. Welcome to Bataiyo."
    : "Your network is waiting.";

  const subtitle = isSuccessful
    ? "Scan the QR code below or grab the app to start asking and recommending."
    : "Get recommendations from people you actually trust. Scan the QR or download from your store.";

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      sx={ui.dialog(isSmallScreen)}
      slotProps={{ backdrop: { style: ui.backdrop } }}
      className="success-mui-dialog"
    >
      {/* ── Header banner ── */}
      <div style={ui.banner}>
        <CloseIcon
          onClick={handleModalClose}
          sx={ui.close}
        />
        <span style={ui.eyebrowPill}>
          <span style={ui.pulse} />
          Bataiyo · Word of Mouth
        </span>
        <h3 style={ui.headline}>{headline}</h3>
        <p style={ui.sub}>{subtitle}</p>
      </div>

      {/* ── Body ── */}
      <div style={ui.body}>
        {/* QR */}
        <div style={ui.qrShell}>
          <span style={ui.qrLabel}>Scan to download</span>
          {isLoading ? (
            <Skeleton variant="rectangular" width={170} height={170} sx={{ borderRadius: "10px" }} />
          ) : (
            <img
              src={QRCode}
              alt="qr-code"
              style={ui.qrImg}
              onLoad={handleImageLoad}
              loading="lazy"
            />
          )}
        </div>

        {/* Divider */}
        <div style={ui.divider}>
          <span style={ui.dividerLine} />
          <span>or get it on</span>
          <span style={ui.dividerLine} />
        </div>

        {/* Store badges */}
        <div style={ui.storeRow}>
          {downloadAppLinks?.map((store) => (
            <a
              key={store?.id}
              href={store?.url}
              target="_blank"
              rel="noreferrer"
            >
              {isLoading ? (
                <Skeleton variant="rectangular" width={140} height={42} sx={{ borderRadius: "8px" }} />
              ) : (
                <img
                  style={ui.storeImg}
                  src={store?.img}
                  alt={store?.alt}
                  loading="lazy"
                  onLoad={handleImageLoad}
                />
              )}
            </a>
          ))}
        </div>

        {/* Social */}
        <div style={ui.socialBlock}>
          <div style={ui.socialLabel}>Follow us</div>
          <div style={ui.socialRow}>
            {dialogSocialIcons?.map((item) => (
              <a
                key={item.id}
                href={item.path}
                target="_blank"
                rel="noreferrer"
              >
                {isLoading ? (
                  <Skeleton variant="circular" width={34} height={34} />
                ) : (
                  <img
                    src={item.icon}
                    alt={item.name}
                    style={ui.socialIcon}
                    onLoad={handleImageLoad}
                    loading="lazy"
                  />
                )}
              </a>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

CustomDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  isSuccessful: PropTypes.bool,
};

export default CustomDialog;
